import * as React from 'react';
import Dropzone, { DropzoneFile } from 'dropzone';
import { ImageUploadResponse } from '../types';
import { DropzoneOptionLabels, getDropzoneOptions } from './dropzoneOptions';
import { getUserManager } from '../../../../../../shared/userManager';
import Api from '../../../../../../shared/Api';

let retryCounts = new Map<string, number>();

export function useDropzone(
    formId: string,
    addFile: (file: DropzoneFile) => void,
    removeFile: (file: DropzoneFile) => void,
    setImageInProgress: (imageUploadResponse: ImageUploadResponse) => void,
    onRetry: () => void,
    labels: DropzoneOptionLabels
) {
    let dropzone = React.useRef<Dropzone | null>(null);

    function handleNotAllowedFileType() {
        // TODO
        console.error('File type not allowed');
    }

    function handleAddedFile(file: DropzoneFile) {
        addFile(file);
    }

    function handleDrop() {
        // ...
    }

    function handleDragLeave() {
        // ...
    }

    function handleQueueComplete() {
        if (dropzone.current === null) {
            return null;
        }

        retryCounts.clear();

        // @ts-ignore
        dropzone.current.options.autoProcessQueue = false;
    }

    function handleRemovedFile(file: DropzoneFile) {
        removeFile(file);
    }

    function handleUploadProgress(file: DropzoneFile, progress: number) {
        // If we want to use progress bars, we can do it here
    }

    function handleSuccess(file: DropzoneFile, imageUploadResponse: ImageUploadResponse) {
        setImageInProgress(imageUploadResponse);
    }

    function handleError(file: DropzoneFile, errorMessage: string | Error) {
        if (
            dropzone.current !== null &&
            typeof errorMessage === 'object' &&
            errorMessage.message === 'Authorization has been denied for this request.'
        ) {
            /**
             * The user has been inactive and the auth token has expired,
             * here we do a silent signin to get a new token and then process the file again.
             */
            let userManager = getUserManager();
            userManager.signinSilent().then(() => {
                let authHeader = new Api().getAuthData();

                // @ts-ignore
                dropzone.current.options.headers = {
                    Authorization: `Bearer ${authHeader.accessToken}`
                };

                onRetry();
                dropzone.current.processFile(file);
            });
            return;
        }

        if (errorMessage === labels.invalidFileType) {
            return;
        }

        let retries = retryCounts.get(file.name) || 0;
        if (retries < 3 && dropzone.current !== null) {
            retryCounts.set(file.name, retries + 1);
            file.status = Dropzone.QUEUED;
            dropzone.current.processFile(file);
            return; 
        }

        // TODO: possibly use snackbar...
        console.error(errorMessage);
    }

    function handleCanceled(file: DropzoneFile) {
        // ...
    }

    React.useEffect(() => {
        // @ts-ignore
        Dropzone.autoDiscover = false;

        if (dropzone.current === null && document.getElementById(formId) !== null) {
            let dropzoneLabels: DropzoneOptionLabels = {
                defaultMessage: labels.defaultMessage,
                fallbackMessage: labels.fallbackMessage,
                cancelUpload: labels.cancelUpload,
                cancelUploadConfirmation: labels.cancelUploadConfirmation,
                invalidFileType: labels.invalidFileType
            };

            dropzone.current = new Dropzone(
                `#${formId}`,
                getDropzoneOptions(
                    dropzoneLabels,
                    handleNotAllowedFileType,
                    handleAddedFile,
                    handleDrop,
                    handleDragLeave,
                    handleQueueComplete,
                    handleRemovedFile,
                    handleUploadProgress,
                    handleSuccess,
                    handleError,
                    handleCanceled
                )
            );
        }
        return () => {
            if (dropzone.current) {
                dropzone.current.off('addedfile', handleAddedFile);
                dropzone.current.off('drop', handleDrop);
                dropzone.current.off('dragleave', handleDragLeave);
                dropzone.current.off('queuecomplete', handleQueueComplete);
                dropzone.current.off('removedfile', handleRemovedFile);
                dropzone.current.off('uploadprogress', handleUploadProgress);
                dropzone.current.off('success', handleSuccess);
                dropzone.current.off('error', handleError);
                dropzone.current.off('canceled', handleCanceled);
                dropzone.current.destroy();
                dropzone.current = null;
            }
        };
    }, []);
    return dropzone;
}
